/*
Changes made:
- Added imports for Brain, Code, Target, and Sparkles icons from lucide-react
*/

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, X } from 'lucide-react';

interface LessonGeneratingPopupProps {
  isOpen: boolean;
  lessonTitle: string;
  onCancel?: () => void;
}

const learningFacts = [
  "Students retain 95% of information when teaching others, compared to 5% through lectures",
  "Taking short breaks every 45-50 minutes improves learning efficiency by up to 20%",
  "Learning a new skill changes your brain structure in as little as 120 minutes",
  "Regular learning reduces cognitive decline risk by up to 63%",
  "The brain processes visual information 60,000 times faster than text",
  "Active learning methods are twice as effective as passive methods",
  "75% of job success comes from soft skills and continuous learning"
];

export function LessonGeneratingPopup({ isOpen, lessonTitle, onCancel }: LessonGeneratingPopupProps) {
  const [progress, setProgress] = useState(0);
  const [currentFact, setCurrentFact] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (isOpen && !isComplete) {
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            setIsComplete(true);
            return 100;
          }
          return prev + 1;
        });
      }, 300);

      const factInterval = setInterval(() => {
        setCurrentFact(prev => (prev + 1) % learningFacts.length);
      }, 5000);

      return () => {
        clearInterval(progressInterval);
        clearInterval(factInterval);
      };
    } else if (!isOpen) {
      setProgress(0);
      setCurrentFact(0);
      setIsComplete(false);
    }
  }, [isOpen, isComplete]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 flex items-center justify-center p-4 z-50"
          >
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl mx-auto relative overflow-hidden">
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200 z-10"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              
              <div className="relative p-4 sm:p-6">
                <div className="flex justify-center mb-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full blur-xl opacity-25 animate-pulse"></div>
                    <div className="relative bg-gradient-to-br from-blue-600 to-blue-500 p-3 rounded-2xl">
                      <BookOpen className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
                
                <h3 className="text-lg sm:text-xl font-semibold text-center text-gray-900 mb-1">
                  Generating Lesson
                </h3>
                <p className="text-gray-600 text-center text-sm mb-2">
                  {lessonTitle}
                </p>
                
                <div className="relative w-24 h-24 mx-auto my-6">
                  <svg className="w-full h-full" viewBox="0 0 100 100">
                    <circle
                      className="text-gray-200"
                      strokeWidth="8"
                      stroke="currentColor"
                      fill="transparent"
                      r="42"
                      cx="50"
                      cy="50"
                    />
                    <circle
                      className="text-blue-600"
                      strokeWidth="8"
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="transparent"
                      r="42"
                      cx="50"
                      cy="50"
                      strokeDasharray={264}
                      strokeDashoffset={264 - (progress / 100) * 264}
                    />
                    <text
                      x="50"
                      y="50"
                      className="text-2xl font-bold"
                      textAnchor="middle"
                      dy=".3em"
                      fill="#2563EB"
                    >
                      {progress}%
                    </text>
                  </svg>
                </div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="bg-blue-50 rounded-lg p-3 mb-4"
                >
                  <motion.p
                    key={currentFact}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="text-sm text-blue-700 text-center"
                  >
                    {learningFacts[currentFact]}
                  </motion.p>
                </motion.div>
                
                <p className="text-sm text-gray-600 text-center">
                  Generating your personalized lesson content...
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}